import { FetcherObject } from "./fetcherSettingsDummyData";
import { isStandardLevel } from "./fetcherSettingsResolver";

export const CSV_COLUMNS = [
  "object_type",
  "object_value",
  "field_type",
  "field_value",
] as const;

export interface ParsedCsvRow {
  /** 1-based line number in the file, header included */
  line: number;
  object_type: string;
  object_value: string;
  field_type: string;
  field_value: string;
  errors: string[];
  warning?: string;
}

export interface ParsedCsv {
  rows: ParsedCsvRow[];
  error: string | null;
}

/** Splits one CSV line, honouring double-quoted values and escaped quotes. */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"') {
      if (inQuotes && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (character === "," && !inQuotes) {
      out.push(current);
      current = "";
    } else {
      current += character;
    }
  }
  out.push(current);
  return out.map((value) => value.trim());
}

/**
 * Validates a pasted or uploaded CSV against the four required columns. The
 * import is all-or-nothing on the endpoint, so this reports every row's own
 * problem rather than stopping at the first.
 */
export function parseDevObjectsCsv(
  text: string,
  existingDevObjects: FetcherObject[],
): ParsedCsv {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (!lines.length) return { rows: [], error: "Nothing pasted." };

  const header = splitCsvLine(lines[0]).map((value) => value.toLowerCase());
  const missing = CSV_COLUMNS.filter((column) => !header.includes(column));
  if (missing.length) {
    return {
      rows: [],
      error: `Missing column${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}`,
    };
  }

  const columnIndex: { [key: string]: number } = {};
  CSV_COLUMNS.forEach((column) => {
    columnIndex[column] = header.indexOf(column);
  });

  const rows = lines.slice(1).map((line, offset) => {
    const parts = splitCsvLine(line);
    const row: ParsedCsvRow = {
      line: offset + 2,
      object_type: parts[columnIndex.object_type] ?? "",
      object_value: parts[columnIndex.object_value] ?? "",
      field_type: parts[columnIndex.field_type] ?? "",
      field_value: parts[columnIndex.field_value] ?? "",
      errors: [],
    };

    if (!row.object_type) row.errors.push("object_type is required");
    if (!row.object_value) row.errors.push("object_value is required");
    if (!row.field_type) row.errors.push("field_type is required");

    if (isStandardLevel(row.object_type)) {
      const side =
        row.object_type === "report_id"
          ? "Reports"
          : row.object_type === "interface_id"
            ? "Interfaces"
            : "brand";
      row.warning = `Standard level - this row will appear on the ${side} side, not in Dev Objects.`;
    }

    const alreadyExists = existingDevObjects.some(
      (existing) =>
        existing.raw === row.object_type &&
        existing.mid === row.object_value &&
        existing.k === row.field_type &&
        existing.v === row.field_value,
    );
    if (alreadyExists) row.errors.push("Identical row already exists");

    return row;
  });

  return { rows, error: null };
}

/** The template offered from the import dialog. */
export const DEV_OBJECTS_CSV_SAMPLE = [
  "object_type,object_value,field_type,field_value",
  "system-default,1,api_request_timeout,30",
  'interface_id,30,url_param,"{""#name"":""forPublishers"",""#value"":""true""}"',
  "report_id,159,select_key,brand_action_date",
].join("\n");
